import React from 'react';
import { FaCheckCircle, FaAward, FaUsers, FaGlobe } from 'react-icons/fa';

const Hero: React.FC = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToServices = () => {
    const element = document.getElementById('services');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <div className="text-white">
            <div className="inline-block bg-blue-500/20 backdrop-blur-sm border border-blue-400/30 rounded-full px-4 py-2 mb-6">
              <span className="text-blue-300 text-sm font-semibold">🌍 Serving US, Canada & EU Markets</span>
            </div>

            <h1 className="text-5xl sm:text-6xl font-bold mb-6 leading-tight">
              Enterprise-Grade
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                Web & Mobile Development
              </span>
            </h1>

            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              We design, build, and scale secure web and mobile applications for growing businesses —
              delivered under flexible B2B contracts with engineering teams that become extensions of yours.
            </p>

            {/* Key Value Props */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
              <div className="flex items-start space-x-3">
                <FaCheckCircle className="text-green-400 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-white">Production-Ready Delivery</h3>
                  <p className="text-gray-400 text-sm">Senior engineers, tested code, deployed solutions</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <FaCheckCircle className="text-green-400 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-white">Full-Stack Expertise</h3>
                  <p className="text-gray-400 text-sm">Backend, frontend, cloud, and mobile capabilities</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <FaCheckCircle className="text-green-400 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-white">B2B Contract Flexibility</h3>
                  <p className="text-gray-400 text-sm">Compliant with US, CA, and EU requirements</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <FaCheckCircle className="text-green-400 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-white">Long-Term Partnership</h3>
                  <p className="text-gray-400 text-sm">Not freelancers — strategic engineering partners</p>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={scrollToContact}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Request a Consultation
              </button>
              <button
                onClick={scrollToServices}
                className="bg-white/10 backdrop-blur-sm text-white border border-white/30 px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-all duration-300"
              >
                View Our Services
              </button>
            </div>
          </div>

          {/* Right Column - Trust Signals */}
          <div className="hidden lg:block">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 shadow-2xl">
              <h3 className="text-white text-2xl font-bold mb-6">Why CTOs Choose Us</h3>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-500/20 p-3 rounded-lg">
                    <FaAward className="text-blue-400 text-2xl" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Senior Engineering Team</h4>
                    <p className="text-gray-300 text-sm">
                      10+ years average experience across Java, .NET, Node.js, React, and cloud platforms
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-blue-500/20 p-3 rounded-lg">
                    <FaUsers className="text-blue-400 text-2xl" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Dedicated Development Teams</h4>
                    <p className="text-gray-300 text-sm">
                      Embedded engineers who learn your business, not rotating contractors
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-blue-500/20 p-3 rounded-lg">
                    <FaGlobe className="text-blue-400 text-2xl" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">International Coverage</h4>
                    <p className="text-gray-300 text-sm">
                      Time-zone overlap with North America and Europe, English-fluent communication
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-white/20">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-3xl font-bold text-blue-400">50+</div>
                    <div className="text-gray-400 text-sm mt-1">Projects Delivered</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-blue-400">98%</div>
                    <div className="text-gray-400 text-sm mt-1">Client Retention</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-blue-400">24/7</div>
                    <div className="text-gray-400 text-sm mt-1">Support Available</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white/50 rounded-full mt-2"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
